import type { HuntJournalEntry } from '@/types/hunt-journal-entry';

interface JournalEntryCardProps {
  entry: HuntJournalEntry;
  waypointName?: string;
  sitLabel?: string;
  authorDisplayName?: string;
}

const TAG_LABELS: Record<string, string> = {
  sighting: 'Sighting',
  weather: 'Weather',
  camp: 'Camp',
  gear: 'Gear',
  note: 'Note',
};

export function JournalEntryCard({
  entry,
  waypointName,
  sitLabel,
  authorDisplayName,
}: JournalEntryCardProps) {
  const formattedTime = new Date(entry.createdAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div
      data-testid={`journal-entry-card-${entry.id}`}
      className="flex flex-col gap-2 p-3 rounded-lg bg-[var(--color-elevated)] border border-[var(--color-border)]"
    >
      {/* Header: tag + time */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {entry.tag && (
            <span
              data-testid="journal-entry-tag"
              className="px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text-secondary)]"
            >
              {TAG_LABELS[entry.tag] ?? entry.tag}
            </span>
          )}
        </div>
        <time
          className="text-body-xs text-[var(--color-text-muted)] shrink-0"
          dateTime={entry.createdAt}
        >
          {formattedTime}
        </time>
      </div>

      {/* Body */}
      <p className="text-body-sm text-[var(--color-text-primary)] whitespace-pre-wrap break-words">
        {entry.body}
      </p>

      {/* Optional metadata: location + sit */}
      {(waypointName ?? sitLabel) && (
        <div className="flex flex-wrap gap-2 mt-0.5">
          {waypointName && (
            <span
              data-testid="journal-entry-location"
              className="text-body-xs text-[var(--color-text-muted)]"
            >
              at {waypointName}
            </span>
          )}
          {sitLabel && (
            <span
              data-testid="journal-entry-sit"
              className="text-body-xs text-[var(--color-text-muted)]"
            >
              during {sitLabel}
            </span>
          )}
        </div>
      )}

      {/* Author attribution */}
      {authorDisplayName && (
        <div
          data-testid="journal-entry-author"
          className="text-body-xs text-[var(--color-text-muted)] mt-0.5"
        >
          {authorDisplayName}
        </div>
      )}
    </div>
  );
}
